import { Box, TextField, Button } from "@mui/material";
import { Add } from "@mui/icons-material";
import Breadcrumb from "@/components/Breadcrumb";
import { useState, useMemo, useEffect } from "react";
import { DefaultSecondaryButton } from "@/styles/globalstyles";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import { usePermissions } from "@/hooks/usePermissions";
import {
  useGetBuyerBossWithBuyerCode,
  useGetBuyerBossList,
  useGetAllBuyerCodes,
  useUpdateBuyerBoss,
  useDeleteBuyerBoss,
} from "@/hooks/buyerBossHooks";
import { getAxiosErrorMessage } from "@/utils/healper";
import { useToast } from "@/hooks/useToast";
import { ConfirmAlert, ErrorAlert } from "@/utils/alert";
import type { BuyerBossWithBuyerCode } from "@/api/buyerBossService";
import BuyerBossTable from "./BuyerBossTable";
import SaveBuyerBossModal from "./SaveBuyerBossModal";

const BuyerBossManagement = () => {
  const canEdit = usePermissions().canEdit("master.buyerboss");
  const [searchFilter, setSearchFilter] = useState("");
  const { showToast } = useToast();
  const [openModal, setOpenModal] = useState(false);
  const [selectedBuyerBoss, setSelectedBuyerBoss] =
    useState<BuyerBossWithBuyerCode | null>(null);

  const {
    data: buyerBossWithCodes,
    isLoading: buyerBossLoading,
    isError: buyerBossIsError,
  } = useGetBuyerBossWithBuyerCode();

  const {
    data: buyerBossList = [],
    isLoading: buyerBossListLoading,
    isError: buyerBossListIsError,
  } = useGetBuyerBossList();
  const {
    data: allBuyerCodes = [],
    isLoading: allBuyerCodesLoading,
    isError: allBuyerCodesIsError,
  } = useGetAllBuyerCodes();

  const { mutate: updateBuyerBoss, isPending: updatePending } =
    useUpdateBuyerBoss();

  const { mutate: deleteBuyerBoss, isPending: deletePending } =
    useDeleteBuyerBoss();

  useEffect(() => {
    document.title = "Master Buyer Manager | AMPA";
  }, []);

  useQueryHandler({
    isFetching:
      updatePending ||
      deletePending ||
      buyerBossListLoading ||
      allBuyerCodesLoading,
    isError: buyerBossListIsError || allBuyerCodesIsError || buyerBossIsError,
  });

  const filteredData = useMemo(() => {
    if (!buyerBossWithCodes) return [];
    if (!searchFilter) return buyerBossWithCodes;

    const lower = searchFilter.toLowerCase();
    return buyerBossWithCodes.filter(
      (item) =>
        item.employee_id.toLowerCase().includes(lower) ||
        item.name.toLowerCase().includes(lower) ||
        item.email.toLowerCase().includes(lower) ||
        item.buyer_code?.some((code) => code.toLowerCase().includes(lower)),
    );
  }, [buyerBossWithCodes, searchFilter]);

  const existingEmployeeIds = useMemo(
    () => buyerBossWithCodes?.map((b) => b.employee_id) ?? [],
    [buyerBossWithCodes],
  );

  const handleAdd = () => {
    setSelectedBuyerBoss(null);
    setOpenModal(true);
  };

  const handleEdit = (row: BuyerBossWithBuyerCode) => {
    setSelectedBuyerBoss(row);
    setOpenModal(true);
  };

  const handleDelete = async (row: BuyerBossWithBuyerCode) => {
    const confirmed = await ConfirmAlert(
      "ยืนยันการลบ",
      `ต้องการลบ ${row.name} (${row.employee_id}) ออกจาก Buyer Manager หรือไม่?`,
      "ยืนยัน",
      "ยกเลิก",
    );
    if (confirmed) {
      deleteBuyerBoss(row.employee_id, {
        onSuccess: () => {
          showToast(`${row.name} ถูกลบออกจาก Buyer Manager แล้ว`, "success");
        },
        onError: (error) => {
          const message = getAxiosErrorMessage(error);
          ErrorAlert(
            "ลบไม่สำเร็จ",
            `เกิดข้อผิดพลาดในการลบ ${row.name}: ${message}`,
          );
        },
      });
    }
  };

  const handleSave = (employeeId: string, buyerCodes: string[]) => {
    updateBuyerBoss(
      { employee_id: employeeId, buyer_code: buyerCodes },
      {
        onSuccess: () => {
          showToast(`ข้อมูล Buyer Manager ถูกบันทึกเรียบร้อยแล้ว`, "success");
        },
        onError: (error) => {
          const message = getAxiosErrorMessage(error);
          ErrorAlert(
            "บันทึกไม่สำเร็จ",
            `เกิดข้อผิดพลาดในการบันทึกข้อมูล: ${message}`,
          );
        },
      },
    );
  };

  return (
    <Box sx={{ px: 3, py: 3 }}>
      <Breadcrumb current="master.buyerboss" />
      <Box sx={{ width: "100%" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <TextField
            label="ค้นหา ชื่อ / Email / รหัสพนักงาน / Buyer Code"
            variant="outlined"
            size="small"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            sx={{
              mt: 2,
              mb: 1,
              width: "40%",
              "& .MuiInputLabel-root": {
                fontSize: "0.8rem",
                color: "#666666",
                "&.Mui-focused": { color: "primary.main" },
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#cccccc" },
                "&:hover fieldset": { borderColor: "#999999" },
              },
            }}
          />
          <Button
            size="small"
            variant="outlined"
            onClick={handleAdd}
            sx={{
              ...DefaultSecondaryButton,
              mt: 2,
              mb: 1,
              alignItems: "center",
            }}
            disabled={!canEdit}
          >
            <Add /> เพิ่ม Buyer Manager
          </Button>
        </Box>
        <Box sx={{ mt: 1, borderRadius: 1 }}>
          <BuyerBossTable
            data={filteredData}
            isLoading={buyerBossLoading}
            canEdit={canEdit}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </Box>
      </Box>

      <SaveBuyerBossModal
        open={openModal}
        handleClose={() => setOpenModal(false)}
        selectedBuyerBoss={selectedBuyerBoss}
        buyerBossList={buyerBossList}
        allBuyerCodes={allBuyerCodes}
        existingEmployeeIds={existingEmployeeIds}
        onSave={handleSave}
      />
    </Box>
  );
};

export default BuyerBossManagement;
